import { observable, action, computed, autorun } from 'mobx';
import { setter } from 'mobx-decorators';
import agent from '../services/web/agent';
import erorrHandler from '../services/web/erorrHandler';
import _ from 'lodash';

class TeamsStore {

    @setter @observable teams;
    @setter @observable displayedTeams = [];
    @observable teamIds = [1];
    @setter @observable teamsTree;
    @setter @observable creatingTeam;
    @setter @observable updatingTeam;
    @setter @observable deletingTeam;
    @setter @observable selectedTeamId;
    @setter @observable selectedTabId;
    @setter @observable teamName;
    @setter @observable editTeamName = '';
    @setter @observable teamUsers = [];
    @setter @observable teamErrorMessage;
    @setter @observable expandedTeams;

    @computed get selectedTeam() {

        if (this.teams == undefined) {
        
            return '';
        }

        const team = this.teams.filter((team) => {

            return team.id == this.selectedTeamId;
        });

        return team.length > 0 ? team[0] : undefined;
    }

    @computed get selectedTeamName() {

        if (this.teams == undefined) {
        
            return '';
        }

        const team = this.teams.filter((team) => {

            return team.id == this.selectedTeamId;
        });

        return team.length > 0 ? team[0].name : '';
    }

    @computed get selectedTeamParentId() {

        if (this.teams == undefined) {
        
            return -1;
        }

        const team = this.teams.filter((team) => {

            return team.id == this.selectedTeamId;
        });

        return team.length > 0 ? team[0].parentId : this.teams[0].id;
    }

    @action getAllTeams() {

        return agent.Teams.getAllTeams()
            .then(action((teams) => {
                this.teams = teams;

                this.initializeSearchTeams();
                
                return this.teams;
            }));
    };
    
    @computed get teamsNumber() {

        if (this.teams) {
        
            return this.teams.length;
        }

        return 0;
    }

    @computed get displayedTeamsNumber() {

        if (this.displayedTeams) {
        
            return this.displayedTeams.length;
        }

        return 0;
    }

    @action addTeam() {

        this.creatingTeam = true;
        this.errors = undefined;
        const selectedTeamId = parseInt(this.selectedTeamId);

        return agent.Teams.addTeam({
                name: this.teamName.trim(),
                parentId: selectedTeamId
            })
            .then(action(() => {

                this.teamName = '';
                this.getAllTeams();
            }))
            .catch(action((err) => {

                this.teamErrorMessage = erorrHandler.getErrorMessage(err);

                throw err;
            }))
            .finally(action(() => {
                this.creatingTeam = false;
            }));
    }

    @action updateTeam(teamId, name, parentId) {

        this.updatingTeam = true;
        this.errors = undefined;
        
        const team = {
            name: name.trim(),
            parentId: parentId
        };

        return agent.Teams.updateTeam(teamId, team)
            .catch(action((err) => {

                this.teamErrorMessage = erorrHandler.getErrorMessage(err);
                throw err;
            }))
            .finally(action(() => {
                this.updatingTeam = false;
            }));
    }

    @action deleteTeam() {

        this.deletingTeam = true;
        this.errors = undefined;
        const selectedTeamId = parseInt(this.selectedTeamId);

        return agent.Teams.deleteTeam(selectedTeamId)
            .then(action(() => {

                this.getAllTeams();
            }))
            .catch(action((err) => {

                this.teamErrorMessage = erorrHandler.getErrorMessage(err);

                throw err;
            }))
            .finally(action(() => {
                this.deletingTeam = false;
            }));
    }

    @action resetTeamIds() {
        this.teamIds = [];
    }

    @action getUserTeamNames(teamIds) {
        let teamNames = [];
        let teamIdArray = String(teamIds).split(',').filter(n => n);
        for (let i = 0; i < teamIdArray.length; i++) {
            let teamResult = this.teams.filter(t => t.id == teamIdArray[i]);
            if (teamResult != null) {
                teamNames.push(teamResult[0].name);
            }
        }
        return teamNames;
    }

    initializeSearchTeams() {
        this.searchTeams();
    }

    searchByText(filterValue){

        var value = filterValue.toLowerCase().trim();
        
        var teamNamesContainingFilterValue = this.teams.filter(x => x.name.toLowerCase().includes(value)).map(t=> t);
        if(teamNamesContainingFilterValue.length == 0)
            return [];

        var filteredTeamsChildren =
        this.teams.filter(t => 
            teamNamesContainingFilterValue.some(ft => 
                t.fullName.toLowerCase().startsWith(ft.fullName.toLowerCase() + "/")));


        var filteredTeamsParents = 
        this.teams.filter(t => 
            teamNamesContainingFilterValue.some(ft => 
                ft.fullName.toLowerCase().startsWith(t.fullName.toLowerCase() + "/" )));
        
        var all = _.union(teamNamesContainingFilterValue, filteredTeamsChildren, filteredTeamsParents);

        this.setExpandedTeams(filteredTeamsParents.map(team => `${team.id}`));
        return all;
    };

    @action searchTeams(searchText) {

        if(!searchText || searchText.trim().length === 0){
            this.expandedTeams = undefined;
            this.displayedTeams = this.teams.map(t => t);
        }
        else{
            this.displayedTeams = this.searchByText(searchText);
        }
    };

    @computed get buildTeamTree() {

        let nodes = [];

        if (this.displayedTeams) {

            this.displayedTeams.map((node, index) => {

                const team = {
                    title: node.name,
                    key: node.id,
                    id: node.id,
                    parentId: node.parentId
                };

                nodes.push(team);
            });

            return this.teamsTree = this.transformToTree(nodes);
        }
    }

    transformToTree(list){

        var map = {}, node, roots = [], i;

        for (i = 0; i < list.length; i += 1) {

            map[list[i].id] = i; // initialize the map
            list[i].children = []; // initialize the children
        }

        for (i = 0; i < list.length; i += 1) {

            node = list[i];

            if (node.parentId !== 0) {
                // if you have dangling branches check that map[node.parentId] exists
                list[map[node.parentId]].children.push(node);
            }
            else {
                roots.push(node);
            }
        }

        return roots;
    }

    @action setTeamIds(teamIds) {

        this.teamIds = teamIds;
    }

    @action getTeamFullName(teamId) {

        if (this.teams == undefined) {
        
            return '';
        }
        
        const teams = this.teams.filter((team) => team.id == teamId);

        if (teams && teams.length > 0) {
            
            return teams[0].fullName;
        }
        
        return '';
    }
}

export default new TeamsStore();